/**
 * Vuex Capacitor Storage Plugin
 * Uses localStorage on web (synchronous) and Capacitor Preferences on native (async)
 */

import { Store } from 'vuex';
import CapacitorService from '@/services/capacitor';
import { LoggingService } from '@/services/logging';

interface CapacitorStorageOptions {
  key?: string;
  paths?: string[];
  reducer?: (state: any, paths: string[]) => any;
  subscriber?: (store: Store<any>) => (handler: (mutation: any, state: any) => void) => void;
}

const defaultOptions: CapacitorStorageOptions = {
  key: 'vuex',
  paths: [],
  reducer: (state, paths) => {
    if (paths.length === 0) return state;

    return paths.reduce((acc, path) => {
      const keys = path.split('.');
      let current = state;

      for (const key of keys) {
        if (current && typeof current === 'object' && key in current) {
          if (!acc[key]) {
            acc[key] = current[key];
          }
          current = current[key];
        }
      }

      return acc;
    }, {} as any);
  },
  subscriber: (store) => (handler) => store.subscribe(handler),
};

const isNative = CapacitorService.isNative();

export function createCapacitorStorage(options: CapacitorStorageOptions = {}) {
  const opts = { ...defaultOptions, ...options };
  const storageKey = opts.key!;

  return (store: Store<any>) => {
    LoggingService.Log('createCapacitorStorage: ' + isNative);
    // Load persisted state on initialization
    if (isNative) {
      restoreStateAsync(store, storageKey, opts);
    } else {
      restoreStateSync(store, storageKey, opts);
    }

    // Subscribe to store mutations to persist state
    const subscriber = opts.subscriber || defaultOptions.subscriber!;
    subscriber(store)((mutation, state) => {
      saveState(state, storageKey, opts);
    });
  };
}

function restoreStateSync(
  store: Store<any>,
  key: string,
  options: CapacitorStorageOptions
) {
  try {
    const savedState = localStorage.getItem(key);

    if (savedState) {
      const state = JSON.parse(savedState);

      if (typeof state === 'object' && state !== null) {
        store.replaceState(merge(store.state, state));
      }
    }
  } catch (error) {
    console.error('Error restoring Vuex state from localStorage:', error);
  } finally {
    // Mark restoration as complete for web (synchronous)
    store.commit('setStateRestored', true);
    LoggingService.Log('restoreStateSync: ' + store.state);
  }
}

async function restoreStateAsync(
  store: Store<any>,
  key: string,
  options: CapacitorStorageOptions
) {
  try {
    const savedState = await CapacitorService.storage.get(key);

    if (savedState) {
      const state = JSON.parse(savedState);

      if (typeof state === 'object' && state !== null) {
        store.replaceState(merge(store.state, state));
      }
    }
  } catch (error) {
    console.error('Error restoring Vuex state from Capacitor storage:', error);
  } finally {
    // Always mark restoration as complete, even if there was an error or no saved state
    store.commit('setStateRestored', true);
  }
}

function saveState(
  state: any,
  key: string,
  options: CapacitorStorageOptions
) {
  try {
    const reducer = options.reducer || defaultOptions.reducer!;
    const paths = options.paths || [];
    const stateToSave = reducer(state, paths);
    const serialized = JSON.stringify(stateToSave);

    if (isNative) {
      CapacitorService.storage.set(key, serialized);
    } else {
      localStorage.setItem(key, serialized);
    }
  } catch (error) {
    console.error('Error saving Vuex state:', error);
  }
}

/**
 * Deep merge two objects
 */
function merge(target: any, source: any): any {
  if (typeof target !== 'object' || target === null) {
    return source;
  }

  if (typeof source !== 'object' || source === null) {
    return target;
  }

  const result = { ...target };

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
        result[key] = merge(target[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
}

export default createCapacitorStorage;
